import { ref, watch, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useForm } from 'vee-validate'
import * as Yup from 'yup'
import { debounce } from 'lodash'
import apiUseCase from '@/usecase/apiUseCase'
import {
  IMandoranCommissionFormValues,
  ITierRow
} from '@/utils/interfaces/iSalesAgentCommission'

export function useMandoranCommissionForm() {
  const store = useStore()
  const app = getCurrentInstance()
  const { $toast } = (app?.appContext.config.globalProperties || {}) as any

  const originalCommissionType = ref<string | null>(null)
  const nameDuplicateError = ref<string>('')

  const initialValues = ref<IMandoranCommissionFormValues>({
    name: '',
    station: null,
    class: null,
    expenseType: null,
    description: '',
    commissionType: null,
    oneTimeCommissionAmount: null,
    passengerCommissionAmount: null
  })

  const schema = Yup.object().shape({
    name: Yup.string()
      .required('Nama Komisi harus diisi')
      .test('not-whitespace-only', 'Nama Komisi tidak boleh hanya menggunakan spasi', (val) => !!val && val.trim().length > 0)
      .min(3, 'Nama Komisi minimal 3 karakter')
      .max(100, 'Nama Komisi maksimal 100 karakter')
      .matches(/^[a-zA-Z0-9\s.\-'&()]+$/, "Nama Komisi hanya boleh menggunakan huruf, angka, spasi, ., -, ', &, (, )")
      .test('unique-name', 'Nama Komisi sudah digunakan', () => !nameDuplicateError.value),
    station: Yup.object()
      .nullable()
      .required('Terminal Asal harus dipilih'),
    class: Yup.object()
      .nullable()
      .required('Kelas harus dipilih'),
    expenseType: Yup.object()
      .nullable()
      .required('Jenis Pengeluaran harus dipilih'),
    description: Yup.string().nullable(),
    commissionType: Yup.string()
      .nullable()
      .required('Pilih salah satu tipe Komisi Per RIT'),
    oneTimeCommissionAmount: Yup.number()
      .transform((val, orig) => (orig === '' || orig === null || Number.isNaN(val) ? null : val))
      .nullable()
      .when('commissionType', {
        is: 'OneTime',
        then: (s) => s.required('Komisi Mandoran Pertama harus diisi')
          .typeError('Komisi Mandoran Pertama harus berupa angka')
          .min(0, 'Komisi minimal Rp 0')
          .max(99999, 'Komisi maksimal Rp 99.999'),
        otherwise: (s) => s.nullable()
      }),
    passengerCommissionAmount: Yup.number()
      .transform((val, orig) => (orig === '' || orig === null || Number.isNaN(val) ? null : val))
      .nullable()
      .required('Komisi Mandoran Per Penumpang harus diisi')
      .typeError('Komisi Mandoran Per Penumpang harus berupa angka')
      .min(0, 'Komisi minimal Rp 0')
      .max(99999, 'Komisi maksimal Rp 99.999')
  })

  const {
    values,
    errors,
    setFieldValue,
    setFieldError,
    validateField,
    handleSubmit,
    resetForm
  } = useForm<IMandoranCommissionFormValues>({
    initialValues: initialValues.value,
    validationSchema: schema
  })

  const checkNameIsDuplicate = async (val: string, currentId?: string): Promise<boolean> => {
    if (!val || val.trim().length < 3) return false
    try {
      const res = await apiUseCase.get(`/management/v2/SalesAgentCommissions?name=${encodeURIComponent(val.trim())}`)
      const dataList = Array.isArray(res.result) ? res.result : (res.result?.Data || [])
      const exactMatch = dataList.filter((x: any) => (x.Name || '').trim().toLowerCase() === val.trim().toLowerCase())
      if (currentId) {
        return exactMatch.length > 0 && val.trim().toLowerCase() !== (initialValues.value.name || '').trim().toLowerCase()
      }
      return exactMatch.length > 0
    } catch (e) {
      return false
    }
  }

  const debouncedCheckUniqueName = debounce(async (val: string, currentId?: string) => {
    if (!val || val.trim().length < 3) return
    if (!/^[a-zA-Z0-9\s.\-'&()]+$/.test(val.trim())) return

    const isDuplicate = await checkNameIsDuplicate(val, currentId)
    if ((values.name || '').trim().toLowerCase() === val.trim().toLowerCase()) {
      if (isDuplicate) {
        nameDuplicateError.value = 'duplicate'
        setFieldError('name', 'Nama Komisi sudah digunakan')
      } else {
        nameDuplicateError.value = ''
        if (errors.value.name === 'Nama Komisi sudah digunakan') {
          setFieldError('name', undefined)
        }
      }
    }
  }, 5000)

  const cancelDebouncedNameCheck = () => {
    debouncedCheckUniqueName.cancel()
  }

  const handleNameInput = (val: string, currentId?: string) => {
    setFieldValue('name', val)
    if (nameDuplicateError.value) {
      nameDuplicateError.value = ''
      if (errors.value.name === 'Nama Komisi sudah digunakan') {
        setFieldError('name', undefined)
      }
    }
    validateField('name')
    debouncedCheckUniqueName(val, currentId)
  }

  const handleFieldChange = (field: any, val: any) => {
    setFieldValue(field, val)
    validateField(field)
  }

  const handleNumberInput = (field: any, e: any) => {
    let num: any = e
    if (e && typeof e === 'object') {
      if ('value' in e) {
        num = e.value
      } else if (e.target && 'value' in e.target) {
        num = e.target.value
      }
    }
    const finalVal = (num !== undefined && num !== null && num !== '' && !Number.isNaN(Number(num)))
      ? Number(num)
      : null
    setFieldValue(field, finalVal)
    validateField(field)
  }
  // Watchers to clear validation errors when user fills in a field
  watch(
    () => values.station,
    () => {
      validateField('station')
    }
  )

  watch(
    () => values.class,
    () => {
      validateField('class')
    }
  )

  watch(
    () => values.expenseType,
    () => {
      validateField('expenseType')
    }
  )

  watch(
    () => values.commissionType,
    () => {
      validateField('commissionType')
      if (values.commissionType === 'OneTime') {
        validateField('oneTimeCommissionAmount')
      }
    }
  )

  watch(
    () => values.oneTimeCommissionAmount,
    () => {
      if (values.commissionType === 'OneTime') {
        validateField('oneTimeCommissionAmount')
      }
    }
  )

  watch(
    () => values.passengerCommissionAmount,
    () => {
      validateField('passengerCommissionAmount')
    }
  )

  const getDataDetail = async (
    id: string,
    onLoadedTiers?: (tiersList: any[], commissionType?: string | null) => void
  ) => {
    store.dispatch('showLoading')
    try {
      const res = await apiUseCase.get(`/management/v2/SalesAgentCommissions/${id}`)
      if (res.result) {
        const data = res.result.Data || res.result

        // Fetch tiers BEFORE resetting form so all values are available
        let tiersList: any[] = []
        try {
          const tiersRes = await apiUseCase.get(`/management/v2/SalesAgentCommissions/${id}/tiers`)
          tiersList = Array.isArray(tiersRes.result) ? tiersRes.result : (tiersRes.result?.Data || [])
        } catch (tierErr) {
          console.error('Failed to get tiers', tierErr)
        }

        // Determine oneTimeCommissionAmount from tiers data
        let oneTimeAmount: number | null = null
        if (data.CommissionType === 'OneTime' && tiersList.length > 0) {
          oneTimeAmount = tiersList[0].CommissionAmount
        }

        initialValues.value = {
          name: data.Name || '',
          station: data.StationId ? { Id: data.StationId, Name: data.StationName, label: data.StationName, value: data.StationId } : null,
          class: data.ClassId ? { Id: data.ClassId, Name: data.ClassName, label: data.ClassName, value: data.ClassId } : null,
          expenseType: data.ExpenseTypeId ? { Id: data.ExpenseTypeId, Name: data.ExpenseTypeName, label: data.ExpenseTypeName, value: data.ExpenseTypeId } : null,
          description: data.Description || '',
          commissionType: data.CommissionType || null,
          oneTimeCommissionAmount: oneTimeAmount,
          passengerCommissionAmount: data.PassengerCommissionAmount ?? null
        }
        resetForm({ values: initialValues.value })
        originalCommissionType.value = data.CommissionType || null

        if (onLoadedTiers) {
          onLoadedTiers(tiersList, data.CommissionType)
        }
      }
    } catch (error: any) {
      if ($toast) {
        $toast.add({
          severity: 'error',
          summary: 'Gagal mengambil data Komisi Mandoran',
          detail: error?.message || error,
          group: 'bc',
          life: 3000
        })
      }
      throw error
    } finally {
      store.dispatch('hideLoading')
    }
  }

  const saveCommission = async (params: {
    commissionId: string | null
    tiers: ITierRow[]
    originalTiers: { id: string; minPassenger?: number; maxPassenger?: number; commissionAmount?: number }[]
  }) => {
    store.dispatch('showLoading')

    try {
      const payload: any = {
        Name: values.name?.trim(),
        CommissionType: values.commissionType,
        StationId: values.station?.Id,
        ClassId: values.class?.Id,
        ExpenseTypeId: values.expenseType?.Id,
        Description: values.description || '-',
        PassengerCommissionAmount: Number(values.passengerCommissionAmount)
      }

      let targetCommissionId: string | null = null

      const isCommissionTypeChanged = Boolean(
        params.commissionId
        && originalCommissionType.value
        && originalCommissionType.value !== values.commissionType
      )

      if (params.commissionId) {
        payload.Id = params.commissionId
        const res = await apiUseCase.put(`/management/v2/SalesAgentCommissions/${params.commissionId}`, payload)
        if (res.error) {
          throw new Error(res.message || 'Gagal mengubah data komisi mandoran')
        }
        targetCommissionId = params.commissionId

        // Jika tipe komisi diubah dari OneTime ke Tiered atau sebaliknya,
        // lakukan DELETE pada data tiers dari tipe komisi lama setelah PUT berhasil (dari paling bawah/terbaru dulu)
        if (isCommissionTypeChanged && params.originalTiers.length > 0) {
          const sortedOldTiers = [...params.originalTiers].sort(
            (a, b) => (b.minPassenger ?? 0) - (a.minPassenger ?? 0)
          )
          await sortedOldTiers.reduce(async (prevPromise, oldTier) => {
            await prevPromise
            if (oldTier.id) {
              const delRes = await apiUseCase.delete(
                `/management/v2/SalesAgentCommissions/${targetCommissionId}/tiers`,
                oldTier.id
              )
              if (delRes.error) {
                console.error('Gagal menghapus tier komisi lama:', oldTier.id, delRes.message)
              }
            }
          }, Promise.resolve())
        }
      } else {
        const res = await apiUseCase.postWithNoId('/management/v2/SalesAgentCommissions', payload)
        if (res.error) {
          throw new Error(res.message || 'Gagal menyimpan data komisi mandoran')
        }
        targetCommissionId = res.result?.Id || res.result?.Data?.Id || res.result?.id || (typeof res.result === 'string' ? res.result : null)
      }

      if (!targetCommissionId) {
        throw new Error('Gagal mendapatkan ID komisi mandoran')
      }

      // Step 2: Bulk PUT Tiers
      let isTiersChanged = false

      if (isCommissionTypeChanged || !params.commissionId) {
        // Tipe berubah, atau data baru -> pasti harus PUT
        isTiersChanged = true
      } else if (values.commissionType === 'OneTime') {
        // Tipe tetap, cek isi tiers-nya
        // Untuk OneTime, cek apakah nominal komisi berubah
        const origAmount = params.originalTiers.length > 0 ? params.originalTiers[0].commissionAmount : null
        if (origAmount !== Number(values.oneTimeCommissionAmount)) {
          isTiersChanged = true
        }
      } else if (values.commissionType === 'Tiered') {
        // Untuk Tiered, bandingkan array tiers dengan originalTiers
        if (params.tiers.length !== params.originalTiers.length) {
          isTiersChanged = true
        } else {
          // Cek setiap baris apakah ada yang beda
          for (let i = 0; i < params.tiers.length; i++) {
            const curr = params.tiers[i]
            const orig = params.originalTiers.find((o) => o.id === curr.id)
            if (!orig
                || orig.minPassenger !== Number(curr.minPassenger)
                || orig.maxPassenger !== Number(curr.maxPassenger)
                || orig.commissionAmount !== Number(curr.commissionAmount)) {
              isTiersChanged = true
              break
            }
          }
        }
      }

      if (isTiersChanged) {
        let tiersPayload: any[] = []

        if (values.commissionType === 'OneTime') {
          // Jika OneTime, kirim 1 baris
          // Gunakan ID dari tier lama (jika ada dan tipe tidak berubah) agar update, jika tidak ID null
          const existingId = (!isCommissionTypeChanged && params.originalTiers.length > 0)
            ? params.originalTiers[0].id
            : null

          tiersPayload = [{
            Id: existingId,
            MinPassengerCount: null,
            MaxPassengerCount: null,
            CommissionAmount: Number(values.oneTimeCommissionAmount)
          }]
        } else if (values.commissionType === 'Tiered') {
          tiersPayload = params.tiers.map((tier) => ({
            Id: tier.id || null, // null -> create new, id -> update existing
            MinPassengerCount: Number(tier.minPassenger),
            MaxPassengerCount: Number(tier.maxPassenger),
            CommissionAmount: Number(tier.commissionAmount)
          }))
        }

        const tierRes = await apiUseCase.put(
          `/management/v2/SalesAgentCommissions/${targetCommissionId}/tiers`,
          tiersPayload
        )
        if (tierRes.error) {
          throw new Error(tierRes.message || 'Gagal menyimpan tier komisi')
        }
      }

      if ($toast) {
        $toast.add({
          severity: 'success',
          detail: params.commissionId ? 'Komisi Mandoran berhasil diubah' : 'Komisi Mandoran berhasil disimpan',
          group: 'bc',
          life: 3000
        })
      }

      return { success: true, isEdit: !!params.commissionId }
    } catch (error: any) {
      if ($toast) {
        $toast.add({
          severity: 'error',
          summary: 'Gagal menyimpan data',
          detail: error.message || error,
          group: 'bc',
          life: 4000
        })
      }
      throw error
    } finally {
      store.dispatch('hideLoading')
    }
  }

  return {
    values,
    errors,
    setFieldValue,
    setFieldError,
    validateField,
    handleSubmit,
    resetForm,
    initialValues,
    nameDuplicateError,
    originalCommissionType,
    checkNameIsDuplicate,
    cancelDebouncedNameCheck,
    handleNameInput,
    handleFieldChange,
    handleNumberInput,
    getDataDetail,
    saveCommission
  }
}
